import { useEffect, useState } from 'react';
import axios from 'axios';
import { Image as ImageIcon, MagnifyingGlassPlus } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { VolunteerPhotoCompare } from './VolunteerPhotoCompare';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Renderer for `derm_scale` question type — URS-F215
 * Looks up the referenced scale, displays options grid + reference-image popup.
 *
 * Props:
 *   scaleId: string — scale_id from question.scale_id
 *   value: string — currently selected option value
 *   onChange: (val) => void
 *   disabled?: boolean
 *   publicMode?: boolean — when true uses /api/public/derm-scales/{id}/image with no credentials
 *   testIdPrefix?: string — used to derive option button testids
 */
export function DermScaleRenderer({ scaleId, value, onChange, disabled = false, publicMode = false, testIdPrefix = 'derm', studyId, volunteerCode, currentVisitCode }) {
  const [scale, setScale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showImage, setShowImage] = useState(false);

  useEffect(() => {
    if (!scaleId) { setLoading(false); return; }
    const url = publicMode
      ? `${API_URL}/api/public/derm-scales/${scaleId}/meta`
      : `${API_URL}/api/config/derm-scales/${scaleId}`;
    const cfg = publicMode ? {} : { withCredentials: true };
    axios.get(url, cfg)
      .then(r => { setScale(r.data); setLoading(false); })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleId]);

  if (loading) return <div className="text-xs text-stone-400 py-2">Cargando escala…</div>;
  if (!scale) return (
    <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-sm p-2">
      Escala no disponible (ID: {scaleId})
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[11px] text-stone-500 italic">{scale.name}</span>
        <div className="flex gap-1.5">
          {!publicMode && studyId && volunteerCode && (
            <VolunteerPhotoCompare
              studyId={studyId}
              volunteerCode={volunteerCode}
              currentVisitCode={currentVisitCode}
              scaleId={scaleId}
              testIdPrefix={`${testIdPrefix}-photo`}
            />
          )}
          {scale.file_id && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid={`${testIdPrefix}-view-image-btn`}
              onClick={() => setShowImage(true)}
              className="h-7 text-xs gap-1 rounded-sm border-violet-300 text-violet-700 hover:bg-violet-50"
            >
              <MagnifyingGlassPlus size={12} />Ver imagen de referencia
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1.5">
        {scale.response_mode === 'numeric' ? null : (scale.options || []).map((opt) => {
          const sel = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              data-testid={`${testIdPrefix}-opt-${opt.value}`}
              onClick={() => onChange(opt.value)}
              title={opt.description}
              className={`flex flex-col items-start text-left px-2 py-2 rounded-sm border transition disabled:cursor-not-allowed ${sel ? 'bg-[#1f3e80] text-white border-[#1f3e80] shadow-sm' : 'bg-white text-stone-700 border-stone-200 hover:border-[#1f3e80]/40'}`}
            >
              <span className="text-[10px] font-mono opacity-70 mb-0.5">{opt.value}</span>
              <span className="text-xs font-semibold leading-tight">{opt.label}</span>
              {opt.description && (
                <span className={`text-[10px] mt-1 leading-tight line-clamp-2 ${sel ? 'text-white/80' : 'text-stone-500'}`}>
                  {opt.description}
                </span>
              )}
            </button>
          );
        })}
      </div>
      {/* URS-F510: modo numerico — input numerico libre con prompt y unidad opcionales. */}
      {scale.response_mode === 'numeric' && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs text-stone-600 block mb-1">
              {scale.numeric_prompt || 'Introduce el valor'}
            </label>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                step="any"
                disabled={disabled}
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                data-testid={`${testIdPrefix}-numeric`}
                className="flex-1 h-9 px-3 bg-white border border-stone-200 rounded-sm text-sm focus:border-[#1f3e80] focus:outline-none disabled:bg-stone-50 disabled:text-stone-400"
                placeholder="0"
              />
              {scale.numeric_unit && (
                <span className="text-sm text-stone-500 whitespace-nowrap">{scale.numeric_unit}</span>
              )}
            </div>
          </div>
        </div>
      )}

      <Dialog open={showImage} onOpenChange={setShowImage}>
        <DialogContent className="max-w-4xl max-h-[92vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon size={18} className="text-violet-600" />
              {scale.name}
            </DialogTitle>
            {scale.description && <DialogDescription className="text-xs">{scale.description}</DialogDescription>}
          </DialogHeader>
          {scale.file_id ? (
            <div className="flex items-center justify-center bg-stone-50 rounded-sm overflow-auto" style={{ maxHeight: '74vh' }}>
              <img
                src={`${API_URL}/api/public/derm-scales/${scale.scale_id}/image`}
                alt={scale.name}
                className="max-w-full"
                data-testid={`${testIdPrefix}-ref-image`}
              />
            </div>
          ) : (
            <p className="text-sm text-stone-500 text-center py-8">Sin imagen de referencia</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
